// Optimistic review-mark writes: changes apply locally at once, are batched
// (debounced) into PUTs of at most MARKS_PER_PUT, and stay persisted until
// the server acknowledges them, so a closed tab or network error never loses
// a mark.
//
// Failures are classified: network errors, 5xx, 408 and 429 retry with
// capped backoff; 401 (session gone) parks the queue with the ops kept for a
// later session; any other 4xx can never succeed, so the batch is dropped and
// reported.
import type { Mark } from '../api/server';
import { HttpError } from '../api/cache';
import { MARKS_PER_PUT } from '../api/limits';

export interface PendingOp {
  reviewed: boolean;
  seq: number;
  /** Unique per write (absent in data persisted before IDs existed). */
  id?: string;
}

export interface PendingMarks {
  ops: Record<string, PendingOp>;
}

export type FailureKind = 'retry' | 'park' | 'drop';

export interface QueueOptions {
  send: (marks: Mark[]) => Promise<void>;
  persist: (pending: PendingMarks) => Promise<void> | void;
  /** Ops the server acknowledged, or dropped as unsendable (`dropped`). */
  settled?: (ops: [string, PendingOp][], how: 'acked' | 'dropped') => Promise<void> | void;
  /** Called on every failed send with how the queue handled it. */
  onError?: (error: unknown, kind: FailureKind) => void;
  delay?: number;
  maxRetryDelay?: number;
}

/** How a failed PUT is handled (see the file comment). */
export function classifyFailure(e: unknown): FailureKind {
  if (!(e instanceof HttpError)) return 'retry';
  if (e.status === 401) return 'park';
  if (e.status >= 500 || e.status === 429 || e.status === 408 || e.status === 0) return 'retry';
  return 'drop';
}

let idCounter = 0;
function newOpId(): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `${rand}-${++idCounter}`;
}

export class MarksQueue {
  private ops = new Map<string, PendingOp>();
  private seq = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inflight: Promise<void> | null = null;
  private retry = 0;
  private parked = false;
  private disposed = false;
  private readonly delay: number;
  private readonly maxRetryDelay: number;

  constructor(private opts: QueueOptions) {
    this.delay = opts.delay ?? 300;
    this.maxRetryDelay = opts.maxRetryDelay ?? 30000;
  }

  /** Restore ops persisted by an earlier session and schedule them. */
  restore(pending: PendingMarks | undefined) {
    if (!pending) return;
    for (const [key, op] of Object.entries(pending.ops)) {
      if (!this.ops.has(key)) this.ops.set(key, { reviewed: op.reviewed, seq: ++this.seq, id: op.id ?? newOpId() });
    }
    if (this.ops.size) this.schedule(this.delay);
  }

  set(blockKey: string, reviewed: boolean) {
    this.ops.set(blockKey, { reviewed, seq: ++this.seq, id: newOpId() });
    void this.opts.persist(this.snapshot());
    this.retry = 0;
    this.parked = false;
    this.schedule(this.delay);
  }

  /** True after a 401: ops are kept but not retried until the next change. */
  get isParked() {
    return this.parked;
  }

  /** Pending (unacknowledged) values, which win over server state. */
  pending(): Map<string, boolean> {
    return new Map([...this.ops].map(([k, v]) => [k, v.reviewed]));
  }

  get size() {
    return this.ops.size;
  }

  snapshot(): PendingMarks {
    return { ops: Object.fromEntries(this.ops) };
  }

  private schedule(ms: number) {
    if (this.parked || this.disposed) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, ms);
  }

  /**
   * Send everything pending now, MARKS_PER_PUT per PUT. Resolves when the
   * attempt finishes: every chunk acknowledged, or one failed. Each chunk
   * settles on its own, so a failure affects only that chunk.
   */
  async flush(): Promise<void> {
    if (this.inflight) {
      await this.inflight;
      if (this.ops.size && !this.timer && !this.disposed) return this.flush();
      return;
    }
    if (!this.ops.size) return;
    const batch = new Map([...this.ops].slice(0, MARKS_PER_PUT));
    const more = this.ops.size > batch.size;
    const marks: Mark[] = [...batch].map(([block_key, op]) => ({ block_key, reviewed: op.reviewed }));
    let settled = false;
    this.inflight = (async () => {
      try {
        await this.opts.send(marks);
        await this.settle(batch, 'acked');
        this.retry = 0;
        settled = true;
      } catch (e) {
        const kind = classifyFailure(e);
        this.opts.onError?.(e, kind);
        if (kind === 'drop') {
          await this.settle(batch, 'dropped');
          settled = true;
        } else if (kind === 'park') {
          this.parked = true;
          if (this.timer) clearTimeout(this.timer);
          this.timer = null;
        } else {
          this.retry++;
          this.schedule(Math.min(this.maxRetryDelay, this.delay * 2 ** this.retry));
        }
      }
    })();
    try {
      await this.inflight;
    } finally {
      this.inflight = null;
    }
    // The next chunk goes at once: it was due with this one. (A retried or
    // parked chunk holds the rest back, so chunks still land in order.)
    if (settled && more && this.ops.size && !this.parked && !this.disposed) return this.flush();
  }

  /** Remove a sent batch's ops, except those changed again while it was in flight. */
  private async settle(batch: Map<string, PendingOp>, how: 'acked' | 'dropped') {
    const done: [string, PendingOp][] = [];
    for (const [key, op] of batch) {
      if (this.ops.get(key)?.seq === op.seq) {
        this.ops.delete(key);
        done.push([key, op]);
      } else if (how === 'acked') done.push([key, op]); // the older write is settled either way
    }
    await this.opts.persist(this.snapshot());
    await this.opts.settled?.(done, how);
  }

  /**
   * Stop sending. An attempt already started finishes, but nothing is
   * retried: unsent ops stay persisted for the next store on this PR or the
   * startup replay.
   */
  dispose() {
    this.disposed = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}
